import { useState } from 'react'
import {
	EuiButton,
	EuiPage,
	EuiPageBody,
	EuiPageSection,
	EuiFlexGroup,
	EuiFlexItem,
	EuiPanel,
} from '@equipmentshare/ds2'
import { SendPlane2LineIcon } from '@equipmentshare/ds2-icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { heapEvent } from '@equipmentshare/heaper'
import { REQUESTS_TABLE_HEIGHT } from 'views/helpers/utils'
import PageTitle from 'views/components/PageTitle/PageTitle'
import StyledHeader from 'views/components/StyledHeader'
import DemandRequestsList from './components/DemandRequestsList'
import StatusFilter from './components/StatusFilter'
import RequestNumberFilter from './components/RequestNumberFilter'
import FulfillmentOrderNumberFilter from './components/FulfillmentOrderNumberFilter'
import AssignedToFilter from './components/AssignedToFilter'
import RequestedByFilter from './components/RequestedByFilter'
import PartPicker from '../../components/PartPicker'
import { Part, Permission } from '../../graphql'
import InventoryLocationsFilter from 'views/components/InventoryLocationsFilter'
import InventoryLocationsSubHeader from 'views/components/InventoryLocationsSubHeader'
import useStoreIds from 'hooks/useStoreIds'
import usePermissions from 'hooks/usePermissions'
import { checkPermissions } from 'components/PermissionsCheck/helpers'
import useRequestsListParams from './hooks/useRequestsListParams'
import useSelectedPart from './hooks/useSelectedPart'
import styles from './requestsView.module.css'

export default function Requests() {
	// Store selection is app-wide state shared with the other inventory views, so
	// it stays in its reactive variable rather than moving into the URL. It is
	// handed to the list params hook because it still filters the result set,
	// which means it also invalidates a page cursor when it changes.
	const { inventoryLocationIds, defaultStoresApplied } = useStoreIds()

	const {
		statuses,
		assignedToIds,
		requestedByIds,
		requestNumberInput,
		requestNumbers,
		fulfillmentOrderNumberInput,
		fulfillmentOrderNumbers,
		partId,
		rowSize,
		paging,
		isFirstPage,
		hasAnyFilter,
		setStatuses,
		setAssignedToIds,
		setRequestedByIds,
		setRequestNumberInput,
		setFulfillmentOrderNumberInput,
		setPartId,
		setRowSize,
		pageForward,
		pageBackward,
		resetPaging,
	} = useRequestsListParams( inventoryLocationIds, defaultStoresApplied )

	// Only an id goes in the URL, but the picker hands over the whole part — keep
	// it so the combo box does not blank out while the lookup round-trips.
	const [ pickedPart, setPickedPart ] = useState<Part | undefined>( undefined )
	const selectedPart = useSelectedPart( partId, pickedPart )

	const navigate = useNavigate()
	const location = useLocation()
	const { userPermissions } = usePermissions( [ Permission.CanCreateDemandRequestByStoreId ] )
	const userCanCreateRequest = checkPermissions( userPermissions, [ Permission.CanCreateDemandRequestByStoreId ] )
	const fullHeight = 'eui-fullHeight'

	const handlePartChange = ( _id: number, part?: Part ) => {
		setPickedPart( part )
		setPartId( part ? String( part.id ) : undefined )
	}

	const onClickStockRequest = () => {
		heapEvent( 'Inventory - Requests List - Click - Stock Request', {} )
		navigate( '/requests/new', { state: { from: `${location.pathname}${location.search}` } } )
	}

	return (
		<EuiPage paddingSize="l">
			<EuiPageBody className={fullHeight}>
				<PageTitle title="Requests" />
				<StyledHeader>
					<EuiFlexGroup wrap>
						<EuiFlexItem>
							<div className="content searchFilter">
								<RequestNumberFilter
									initialValue={requestNumberInput}
									onChange={setRequestNumberInput}
								/>
								<FulfillmentOrderNumberFilter
									initialValue={fulfillmentOrderNumberInput}
									onChange={setFulfillmentOrderNumberInput}
								/>
								<InventoryLocationsFilter />
								<StatusFilter
									selectedStatuses={statuses}
									onChange={setStatuses}
								/>
								<AssignedToFilter
									selectedIds={assignedToIds}
									onChange={setAssignedToIds}
								/>
								<RequestedByFilter
									selectedIds={requestedByIds}
									onChange={setRequestedByIds}
								/>
								<PartPicker
									hideLabel={true}
									className={styles.partPicker}
									selectedPart={selectedPart}
									onChange={handlePartChange}
									placeholder="Search or select manufacturer #"
								/>
							</div>
						</EuiFlexItem>
						{userCanCreateRequest && (
							<EuiFlexItem grow={false}>
								<div className="content actions">
									<EuiButton
										fill
										iconType={SendPlane2LineIcon}
										onClick={onClickStockRequest}
									>
										Stock Request
									</EuiButton>
								</div>
							</EuiFlexItem>
						)}
					</EuiFlexGroup>
				</StyledHeader>
				<InventoryLocationsSubHeader pageTitle="Requests" />
				<EuiPageSection
					paddingSize="none"
					className={fullHeight}
				>
					<EuiPanel
						paddingSize="none"
						hasBorder
						hasShadow={false}
						className="eui-yScroll"
						style={{ height: REQUESTS_TABLE_HEIGHT }}
					>
						<DemandRequestsList
							statuses={statuses.length > 0 ? statuses : undefined}
							requestingInventoryIds={inventoryLocationIds}
							assignedToIds={assignedToIds.length > 0 ? assignedToIds : undefined}
							requestedByUserIds={requestedByIds.length > 0 ? requestedByIds : undefined}
							requestNumbers={requestNumbers}
							fulfillmentOrderNumbers={fulfillmentOrderNumbers}
							partIds={partId ? [ partId ] : undefined}
							paging={paging}
							rowSize={rowSize}
							isFirstPage={isFirstPage}
							hasAnyFilter={hasAnyFilter}
							pageForward={pageForward}
							pageBackward={pageBackward}
							setRowSize={setRowSize}
							resetPaging={resetPaging}
						/>
					</EuiPanel>
				</EuiPageSection>
			</EuiPageBody>
		</EuiPage>
	)
}
